// `extension-preferences-form.tpl.html` — the four toggles, with the ATS name folded into
// every label.
//
// ⚠️ EACH TOGGLE IS TRI-STATE AND THE THIRD STATE HAS ITS OWN LOOK. `not-set` replaces the
// knob with a yellow "Default" pill (`_extension-preferences-form.scss:30`), and the × beside
// it puts the preference back to `null`. Treating absent as `false` would draw a plain "off"
// switch for a user who has never chosen, and the first toggle would then read as a change
// from a value they never set.
import type { ReactElement, ReactNode } from 'react'
import type { ExtensionPreferences } from '@woo-ext/api'
import { WooSwitch } from '../common/WooSwitch.tsx'

/** The four keys, in the template's order. `autoOpenSearchesOnIndeed` is flag-gated. */
export type PreferenceKey =
  | 'autoOpenCandidatesOnATS'
  | 'autoOpenCandidatesOnLinkedIn'
  | 'autoOpenSearchesOnLinkedIn'
  | 'autoOpenSearchesOnIndeed'

interface PreferenceRow {
  key: PreferenceKey
  /** The wrapper class upstream puts on the row — three of the four disagree with the key. */
  cssClass: string
  title: string
  subtitle: (atsLabel: string) => string
}

const ROWS: readonly PreferenceRow[] = [
  {
    key: 'autoOpenCandidatesOnATS',
    cssClass: 'auto-open-candidate-in-ats',
    title: 'On candidate in $$ATS_NAME$$',
    subtitle: () => 'When Woo has updated information about them.',
  },
  {
    key: 'autoOpenCandidatesOnLinkedIn',
    cssClass: 'auto-open-ats-info-on-li',
    title: 'On person in LinkedIn',
    subtitle: (ats) => `When they exist in ${ats}.`,
  },
  {
    key: 'autoOpenSearchesOnLinkedIn',
    cssClass: 'auto-open-searches-on-li',
    title: 'On LinkedIn search',
    subtitle: (ats) => `With an equivalent ${ats} search.`,
  },
  {
    key: 'autoOpenSearchesOnIndeed',
    cssClass: 'auto-open-searches-on-indeed',
    title: 'On Indeed search',
    subtitle: (ats) => `With an equivalent ${ats} search.`,
  },
]

export interface ExtensionPreferencesFormProps {
  preferences: ExtensionPreferences
  /** `getAtsLabel($window)` — substituted into three of the four labels (5cfd3ef899). */
  atsLabel?: string
  /** `featureFlags[ExtensionIndeedSearchEnabled]` — the fourth row is behind it. */
  indeedSearchEnabled?: boolean
  disabled?: boolean
  /** `showNotSet` — draw the "Default" pill. Off in the header tooltip, on in the admin form. */
  showNotSet?: boolean
  allowResetToDefault?: boolean
  onChange?: (preferences: ExtensionPreferences) => void
}

function label(row: PreferenceRow, atsLabel: string): ReactNode {
  return (
    <span className="switch-label-inner">
      <b className="switch-label-bold">{row.title.replace(/\$\$ATS_NAME\$\$/g, atsLabel)}</b>
      <span className="switch-label-small">{row.subtitle(atsLabel)}</span>
    </span>
  )
}

export function ExtensionPreferencesForm({
  preferences,
  atsLabel = '',
  indeedSearchEnabled = false,
  disabled = false,
  showNotSet = false,
  allowResetToDefault = false,
  onChange,
}: ExtensionPreferencesFormProps): ReactElement {
  const rows = ROWS.filter((row) => row.key !== 'autoOpenSearchesOnIndeed' || indeedSearchEnabled)

  return (
    <div className="extension-preferences-form">
      {rows.map((row) => {
        const value = preferences[row.key]
        const unset = value === undefined || value === null
        return (
          <div className={`switch-preference ${row.cssClass}`} key={row.key}>
            <WooSwitch
              className={`switch-small${showNotSet && unset ? ' not-set' : ''}`}
              isOn={value === true}
              disabled={disabled}
              labelNode={label(row, atsLabel)}
              onToggle={() => {
                onChange?.({ ...preferences, [row.key]: value !== true })
              }}
            />
            {disabled || !allowResetToDefault || unset ? null : (
              <i
                className="reset-to-default fa fa-times"
                title="Reset to default"
                role="button"
                tabIndex={0}
                onClick={() => {
                  onChange?.({ ...preferences, [row.key]: null })
                }}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return
                  event.preventDefault()
                  onChange?.({ ...preferences, [row.key]: null })
                }}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}
